import React, { useEffect, useState, useMemo } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  doc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import TaskModal from "../components/TaskModal";

type StatusFilter = "all" | "active" | "test" | "inactive";

const COLUMN_KEYS = [
  "icon",
  "title",
  "description",
  "active",
  "category",
  "subcategory",
  "type",
  "visibility",
  "classId",
  "id",
  "bounty",
  "oneTime",
  "reward",
  "cooldownSeconds",
  "itemType",
  "pendingCount",
  "approvedCount",
  "taskKind",
  "addedDate",
  "nextTaskId",
  "tags",
] as const;

const COLUMN_LABELS: Record<string, string> = {
  icon: "Icon",
  title: "Title",
  description: "Description",
  active: "Active",
  category: "Category",
  subcategory: "Subcategory",
  type: "Type",
  visibility: "Language",
  classId: "Class ID",
  id: "ID",
  bounty: "Bounty",
  oneTime: "One-time",
  reward: "Reward",
  cooldownSeconds: "Cooldown",
  itemType: "Item type",
  pendingCount: "Pending",
  approvedCount: "Approved",
  taskKind: "Task kind",
  addedDate: "Added date",
  nextTaskId: "Next task",
  tags: "Tags",
};

const LANGUAGE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All languages" },
  { value: "en", label: "English" },
  { value: "vi", label: "Vietnamese" },
  { value: "es", label: "Spanish" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "tl", label: "Filipino" },
  { value: "hi", label: "हिन्दी" },
  { value: "pt", label: "Português (Brasil)" },
  { value: "th", label: "ภาษาไทย" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "it", label: "Italiano" },
];

const defaultColumnVisibility = () => Object.fromEntries(COLUMN_KEYS.map((k) => [k, true]));

export default function Overview() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubcategory, setFilterSubcategory] = useState("");
  const [filterItemType, setFilterItemType] = useState("");
  const [filterTaskKind, setFilterTaskKind] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(defaultColumnVisibility);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({
    id: "",
    title: "",
    classId: "",
    description: "",
    category: "",
    subcategory: "",
    type: "",
    active: "",
    bounty: "",
    oneTime: "",
    reward: "",
    cooldownSeconds: "",
    itemType: "",
    pendingCount: "",
    approvedCount: "",
    taskKind: "",
    addedDate: "",
    nextTaskId: "",
    tags: "",
    visibility: "",
  });
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalMode, setTaskModalMode] = useState<"create" | "edit">("create");
  const [taskModalId, setTaskModalId] = useState<string | undefined>(undefined);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [changeStatusValue, setChangeStatusValue] = useState<"active" | "test" | "inactive">("active");
  const [batchSaving, setBatchSaving] = useState(false);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const tasksSnap = await getDocs(collection(db, "tasks"));
      const tasksData = await Promise.all(
        tasksSnap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const submissionsSnap = await getDocs(
            query(
              collection(db, "submissions"),
              where("taskId", "==", docSnap.id)
            )
          );
          const pendingCount = submissionsSnap.docs.filter(
            (d) => d.data().status === "pending"
          ).length;
          const approvedCount = submissionsSnap.docs.filter(
            (d) => d.data().status === "approved"
          ).length;
          return {
            id: docSnap.id,
            ...data,
            pendingCount: pendingCount ?? 0,
            approvedCount: approvedCount ?? 0,
          };
        })
      );
      setTasks(tasksData);
    } catch (e) {
      console.error("Error fetching tasks:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter]);

  const classId = (t: any) =>
    t?.classification?.classId ?? t?.detection?.schemaRef?.id ?? "";

  const iconUrl = (t: any) =>
    t?.icon || t?.sampleImageUrl || t?.sampleMedia?.primary?.[0] || "";

  const filteredByStatus = useMemo(() => {
    if (statusFilter === "all") return tasks;
    return tasks.filter((t) => {
      if (statusFilter === "active") return t.active === true && t.mode !== "test";
      if (statusFilter === "inactive") return t.active === false;
      if (statusFilter === "test") return t.mode === "test";
      return true;
    });
  }, [tasks, statusFilter]);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    filteredByStatus.forEach((t) => {
      const v = (t.type || "Photo").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [filteredByStatus]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    filteredByStatus.forEach((t) => {
      const c = (t.category || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [filteredByStatus]);

  const subcategoryOptions = useMemo(() => {
    if (!filterCategory) return [];
    const set = new Set<string>();
    filteredByStatus.forEach((t) => {
      if ((t.category || "").trim() !== filterCategory) return;
      const s = (t.subcategory || "").trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [filteredByStatus, filterCategory]);

  const itemTypeOptions = useMemo(() => {
    let base = filteredByStatus;
    if (filterCategory) base = base.filter((t) => (t.category || "").trim() === filterCategory);
    if (filterSubcategory) base = base.filter((t) => (t.subcategory || "").trim() === filterSubcategory);
    const set = new Set<string>();
    base.forEach((t) => {
      const i = (t.itemType || "").trim();
      if (i) set.add(i);
    });
    return Array.from(set).sort();
  }, [filteredByStatus, filterCategory, filterSubcategory]);

  const filteredBySearchAndColumns = useMemo(() => {
    return filteredByStatus.filter((t) => {
      if (filterType && (t.type || "Photo").trim() !== filterType) return false;
      if (filterCategory && (t.category || "").trim() !== filterCategory) return false;
      if (filterSubcategory && (t.subcategory || "").trim() !== filterSubcategory) return false;
      if (filterItemType && (t.itemType || "").trim() !== filterItemType) return false;
      if (filterLanguage) {
        const langs = t.visibility?.languages ?? ["all"];
        if (!langs.includes(filterLanguage) && !langs.includes("all")) return false;
      }
      if (filterTaskKind) {
        if (filterTaskKind === "multi") {
          if (!["before_after", "custom"].includes((t.taskKind || "single").trim())) return false;
        } else if ((t.taskKind || "single").trim() !== filterTaskKind) return false;
      }
      const searchLower = globalSearch.toLowerCase();
      const searchable = [
        t.title,
        t.description,
        t.category,
        t.subcategory,
        t.type,
        classId(t),
        t.id,
        t.active ? "yes active" : "no inactive",
        t.bounty ? "yes bounty" : "no",
        t.oneTime ? "yes" : "no",
        String(t.reward ?? ""),
        String(t.cooldownSeconds ?? ""),
        t.itemType,
        String(t.pendingCount ?? ""),
        String(t.approvedCount ?? ""),
        t.taskKind,
        t.addedDate,
        t.nextTaskId,
        Array.isArray(t.tags) ? t.tags.join(" ") : String(t.tags ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (globalSearch && !searchable.includes(searchLower)) return false;
      const cf = columnFilters;
      if (cf.title && !String(t.title || "").toLowerCase().includes(cf.title.toLowerCase())) return false;
      if (cf.description && !String(t.description || "").toLowerCase().includes(cf.description.toLowerCase())) return false;
      if (cf.active && !(t.active ? "yes" : "no").toLowerCase().includes(cf.active.toLowerCase())) return false;
      if (cf.category && !String(t.category || "").toLowerCase().includes(cf.category.toLowerCase())) return false;
      if (cf.subcategory && !String(t.subcategory || "").toLowerCase().includes(cf.subcategory.toLowerCase())) return false;
      if (cf.type && !String(t.type || "").toLowerCase().includes(cf.type.toLowerCase())) return false;
      if (cf.classId && !String(classId(t) || "").toLowerCase().includes(cf.classId.toLowerCase())) return false;
      if (cf.id && !String(t.id || "").toLowerCase().includes(cf.id.toLowerCase())) return false;
      if (cf.bounty && !(t.bounty ? "yes" : "no").toLowerCase().includes(cf.bounty.toLowerCase())) return false;
      if (cf.oneTime && !(t.oneTime ? "yes" : "no").toLowerCase().includes(cf.oneTime.toLowerCase())) return false;
      if (cf.reward && !String(t.reward ?? "").includes(cf.reward)) return false;
      if (cf.cooldownSeconds && !String(t.cooldownSeconds ?? "").includes(cf.cooldownSeconds)) return false;
      if (cf.itemType && !String(t.itemType || "").toLowerCase().includes(cf.itemType.toLowerCase())) return false;
      if (cf.pendingCount && !String(t.pendingCount ?? "").includes(cf.pendingCount)) return false;
      if (cf.approvedCount && !String(t.approvedCount ?? "").includes(cf.approvedCount)) return false;
      if (cf.taskKind && !String(t.taskKind || "").toLowerCase().includes(cf.taskKind.toLowerCase())) return false;
      if (cf.addedDate && !String(t.addedDate || "").toLowerCase().includes(cf.addedDate.toLowerCase())) return false;
      if (cf.nextTaskId && !String(t.nextTaskId || "").toLowerCase().includes(cf.nextTaskId.toLowerCase())) return false;
      if (cf.tags && !(Array.isArray(t.tags) ? t.tags.join(" ") : String(t.tags ?? "")).toLowerCase().includes(cf.tags.toLowerCase())) return false;
      return true;
    });
  }, [filteredByStatus, globalSearch, columnFilters, filterType, filterCategory, filterSubcategory, filterItemType, filterLanguage, filterTaskKind]);

  const visibleColumns = useMemo(
    () => COLUMN_KEYS.filter((k) => columnVisibility[k] !== false),
    [columnVisibility]
  );

  const iconVisible = columnVisibility.icon !== false;
  const titleStickyLeft = iconVisible ? "left-[6.5rem]" : "left-10";

  const getStickyClasses = (key: string, isHeader: boolean, isFilterRow: boolean) => {
    const top = isFilterRow ? "top-[2.5rem]" : "top-0";
    const bg = isFilterRow ? "bg-gray-50" : "bg-gray-100";
    const z = "z-40";
    const shadow = "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]";
    if (key === "icon") return `sticky left-10 ${top} ${z} ${bg} ${shadow}`;
    if (key === "title") return `sticky ${titleStickyLeft} ${top} ${z} ${bg} ${shadow}`;
    return isHeader ? `sticky ${top} z-30 ${bg}` : "";
  };

  const getBodyCellStickyClasses = (key: string, selected?: boolean) => {
    const bg = selected ? "bg-inherit" : "bg-white";
    if (key === "icon") return `sticky left-10 z-10 ${bg} shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]`;
    if (key === "title") return `sticky ${titleStickyLeft} z-10 min-w-[140px] ${bg} shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]`;
    return "";
  };

  const getCellContent = (task: any, key: string) => {
    switch (key) {
      case "icon":
        return iconUrl(task) ? (
          <img src={iconUrl(task)} alt="" className="w-10 h-10 object-contain rounded bg-gray-50" />
        ) : (
          <span className="text-gray-300 text-xs block w-10 h-10 flex items-center justify-center bg-gray-50 rounded">—</span>
        );
      case "title": return task.title ?? "";
      case "description": return <span className="max-w-xs truncate block">{task.description ?? ""}</span>;
      case "active": return task.active ? "Yes" : "No";
      case "category": return task.category ?? "";
      case "subcategory": return task.subcategory ?? "";
      case "type": return task.type ?? "Photo";
      case "visibility": return (task.visibility?.languages?.length ? task.visibility.languages.join(", ") : "all");
      case "classId": return <span className="font-mono text-sm">{classId(task)}</span>;
      case "id": return <span className="font-mono text-sm">{task.id}</span>;
      case "bounty": return task.bounty ? "Yes" : "No";
      case "oneTime": return task.oneTime ? "Yes" : "No";
      case "reward": return task.reward ?? "";
      case "cooldownSeconds": return task.cooldownSeconds ?? "";
      case "itemType": return task.itemType ?? "";
      case "pendingCount": return task.pendingCount ?? "";
      case "approvedCount": return task.approvedCount ?? "";
      case "taskKind": return task.taskKind ?? "single";
      case "addedDate": return task.addedDate ? new Date(task.addedDate).toLocaleDateString() : "";
      case "nextTaskId": return <span className="font-mono text-sm max-w-[120px] truncate block">{task.nextTaskId ?? ""}</span>;
      case "tags": return <span className="max-w-[100px] truncate text-sm block">{Array.isArray(task.tags) ? task.tags.join(", ") : ""}</span>;
      default: return "";
    }
  };

  const stats = useMemo(() => {
    const total = tasks.length;
    const active = tasks.filter((t) => t.active === true && t.mode !== "test").length;
    const inactive = tasks.filter((t) => t.active === false).length;
    const test = tasks.filter((t) => t.mode === "test").length;
    return { total, active, test, inactive };
  }, [tasks]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredBySearchAndColumns.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBySearchAndColumns.map((t) => t.id)));
    }
  };

  const openCreateTask = () => {
    setTaskModalMode("create");
    setTaskModalId(undefined);
    setTaskModalOpen(true);
  };

  const openEditTask = () => {
    const id = Array.from(selectedIds)[0];
    if (!id) return;
    setTaskModalMode("edit");
    setTaskModalId(id);
    setTaskModalOpen(true);
  };

  const handleTaskModalClose = () => {
    setTaskModalOpen(false);
    setTaskModalId(undefined);
  };

  const handleTaskModalSave = () => {
    fetchTasks();
    setSelectedIds(new Set());
    handleTaskModalClose();
  };

  const handleBatchChangeStatus = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchSaving(true);
    try {
      const batch = writeBatch(db);
      for (const id of ids) {
        const ref = doc(db, "tasks", id);
        if (changeStatusValue === "active") {
          batch.update(ref, { active: true, mode: "default" });
        } else if (changeStatusValue === "inactive") {
          batch.update(ref, { active: false });
        } else {
          batch.update(ref, { mode: "test", active: true });
        }
      }
      await batch.commit();
      fetchTasks();
      setSelectedIds(new Set());
      setChangeStatusOpen(false);
    } catch (e) {
      console.error("Batch update failed:", e);
    } finally {
      setBatchSaving(false);
    }
  };

  const exportCsv = () => {
    const csvCell = (t: any, key: string): string => {
      switch (key) {
        case "icon": return iconUrl(t) || "";
        case "title": return t.title ?? "";
        case "description": return t.description ?? "";
        case "active": return t.active ? "Yes" : "No";
        case "category": return t.category ?? "";
        case "subcategory": return t.subcategory ?? "";
        case "type": return t.type ?? "Photo";
        case "visibility": return (t.visibility?.languages?.length ? t.visibility.languages.join(", ") : "all");
        case "classId": return classId(t);
        case "id": return t.id ?? "";
        case "bounty": return t.bounty ? "Yes" : "No";
        case "oneTime": return t.oneTime ? "Yes" : "No";
        case "reward": return String(t.reward ?? "");
        case "cooldownSeconds": return String(t.cooldownSeconds ?? "");
        case "itemType": return t.itemType ?? "";
        case "pendingCount": return String(t.pendingCount ?? "");
        case "approvedCount": return String(t.approvedCount ?? "");
        case "taskKind": return t.taskKind ?? "single";
        case "addedDate": return t.addedDate ?? "";
        case "nextTaskId": return t.nextTaskId ?? "";
        case "tags": return Array.isArray(t.tags) ? t.tags.join(", ") : String(t.tags ?? "");
        default: return "";
      }
    };
    const headers = visibleColumns.map((k) => COLUMN_LABELS[k]);
    const rows = filteredBySearchAndColumns.map((t) => visibleColumns.map((k) => csvCell(t, k)));
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `tasks_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const oneSelected = selectedIds.size === 1;
  const manySelected = selectedIds.size > 1;

  return (
    <div className="flex flex-col gap-4 min-h-0 min-w-0 h-[calc(100vh-5rem)] overflow-hidden w-full max-w-full">
      <h1 className="text-3xl font-bold flex-shrink-0 truncate">Tasks Overview</h1>

      {/* Status tiles - shrink with window */}
      <div className="grid gap-2 sm:gap-3 flex-shrink-0 min-w-0 grid-cols-[repeat(2,minmax(0,1fr))] sm:grid-cols-[repeat(4,minmax(0,1fr))]">
        {[
          { key: "active" as StatusFilter, label: "Active Tasks", value: stats.active },
          { key: "test" as StatusFilter, label: "Test Tasks", value: stats.test },
          { key: "inactive" as StatusFilter, label: "Inactive Tasks", value: stats.inactive },
          { key: "all" as StatusFilter, label: "Total Tasks", value: stats.total },
        ].map(({ key, label, value }) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatusFilter(key)}
            className={`rounded-lg border-2 p-2 sm:p-4 text-left transition-colors min-w-0 overflow-hidden ${
              statusFilter === key
                ? "border-[#00e3ff] bg-[#00e3ff]/10"
                : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
          >
            <div className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{value}</div>
            <div className="text-xs sm:text-sm text-gray-600 truncate">{label}</div>
          </button>
        ))}
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-3 flex-shrink-0 min-w-0">
        {selectedIds.size === 0 && (
          <>
            <button
              type="button"
              onClick={openCreateTask}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              + Task
            </button>
            <button
              type="button"
              onClick={() => navigate("/tasks/categories")}
              className="px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
            >
              + Category
            </button>
            <button
              type="button"
              onClick={() => navigate("/tasks/subcategories")}
              className="px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
            >
              + Subcategory
            </button>
          </>
        )}
        {oneSelected && (
          <>
            <button
              type="button"
              onClick={openEditTask}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setChangeStatusOpen(true)}
              className="px-4 py-2 rounded bg-amber-600 text-white hover:bg-amber-700"
            >
              Change Status
            </button>
          </>
        )}
        {manySelected && (
          <>
            <button
              type="button"
              disabled
              className="px-4 py-2 rounded bg-gray-300 text-gray-500 cursor-not-allowed"
              title="Select only one task to edit"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setChangeStatusOpen(true)}
              className="px-4 py-2 rounded bg-amber-600 text-white hover:bg-amber-700"
            >
              Change Status
            </button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setColumnsDropdownOpen((o) => !o)}
              className="px-4 py-2 rounded border border-gray-400 text-gray-700 hover:bg-gray-100"
            >
              Columns
            </button>
            {columnsDropdownOpen && (
              <>
                <div className="fixed inset-0 z-[100]" onClick={() => setColumnsDropdownOpen(false)} aria-hidden="true" />
                <div className="absolute right-0 top-full mt-1 z-[101] w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2 max-h-80 overflow-y-auto">
                  <div className="px-3 py-1.5 border-b border-gray-100 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setColumnVisibility(defaultColumnVisibility())}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Show all
                    </button>
                    <button
                      type="button"
                      onClick={() => setColumnVisibility(Object.fromEntries(COLUMN_KEYS.map((k) => [k, false])))}
                      className="text-sm text-gray-500 hover:underline"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="px-2 py-1">
                    {COLUMN_KEYS.map((key) => (
                      <label key={key} className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={columnVisibility[key] !== false}
                          onChange={(e) => setColumnVisibility((prev) => ({ ...prev, [key]: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">{COLUMN_LABELS[key]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="px-4 py-2 rounded border border-gray-400 text-gray-700 hover:bg-gray-100"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters + Search row */}
      <div className="flex-shrink-0 min-w-0 w-full flex flex-wrap items-center gap-2">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-32"
        >
          <option value="">All types</option>
          {typeOptions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setFilterSubcategory("");
            setFilterItemType("");
          }}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-40"
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={filterSubcategory}
          onChange={(e) => {
            setFilterSubcategory(e.target.value);
            setFilterItemType("");
          }}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-40 disabled:opacity-50"
          disabled={!filterCategory}
        >
          <option value="">All subcategories</option>
          {subcategoryOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={filterItemType}
          onChange={(e) => setFilterItemType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-40 disabled:opacity-50"
          disabled={!filterCategory}
        >
          <option value="">All item types</option>
          {itemTypeOptions.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
        <select
          value={filterTaskKind}
          onChange={(e) => setFilterTaskKind(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-44"
        >
          <option value="">All task types</option>
          <option value="single">Single</option>
          <option value="expiration">Expiration</option>
          <option value="multi">Multiple Step</option>
        </select>
        <select
          value={filterLanguage}
          onChange={(e) => setFilterLanguage(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-0 w-44"
        >
          {LANGUAGE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || "all-langs"} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search..."
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table - scroll lives only inside this box; left columns stay pinned */}
      {loading ? (
        <div className="py-8 text-gray-500 flex-shrink-0">Loading...</div>
      ) : (
        <div className="flex-1 min-h-0 min-w-0 w-full max-w-full border rounded overflow-auto">
          <table className="min-w-max border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="sticky left-0 top-0 z-40 p-2 border w-10 bg-gray-100 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                  <input
                    type="checkbox"
                    checked={
                      filteredBySearchAndColumns.length > 0 &&
                      selectedIds.size === filteredBySearchAndColumns.length
                    }
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                {visibleColumns.map((key) => (
                  <th
                    key={key}
                    className={`p-2 border text-left ${key === "icon" ? "p-1 w-16" : ""} ${key === "title" ? "min-w-[140px]" : ""} ${getStickyClasses(key, true, false)}`}
                  >
                    {COLUMN_LABELS[key]}
                  </th>
                ))}
              </tr>
              <tr className="bg-gray-50">
                <th className="sticky left-0 top-[2.5rem] z-40 p-1 border bg-gray-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" />
                {visibleColumns.map((key) => (
                  <th
                    key={key}
                    className={`p-1 border ${key === "title" ? "min-w-[140px]" : ""} ${getStickyClasses(key, true, true)}`}
                  >
                    <input
                      type="text"
                      placeholder={`Filter ${key}`}
                      value={columnFilters[key] ?? ""}
                      onChange={(e) => setColumnFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="w-full text-sm px-1 py-0.5 border border-gray-200 rounded"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredBySearchAndColumns.map((task) => (
                <tr
                  key={task.id}
                  className={`border-b cursor-pointer hover:bg-gray-50 ${selectedIds.has(task.id) ? "bg-blue-50 hover:bg-blue-100" : ""}`}
                  onClick={() => toggleSelect(task.id)}
                >
                  <td className="sticky left-0 z-10 p-2 border w-10 bg-inherit shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(task.id)}
                      onChange={() => toggleSelect(task.id)}
                      aria-label={`Select ${task.id}`}
                    />
                  </td>
                  {visibleColumns.map((key) => (
                    <td
                      key={key}
                      className={`p-2 border ${key === "icon" ? "p-1 w-16" : ""} ${getBodyCellStickyClasses(key, selectedIds.has(task.id))}`}
                    >
                      {getCellContent(task, key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredBySearchAndColumns.length === 0 && !loading && (
        <p className="text-gray-500 py-4">No tasks match the current filters.</p>
      )}

      {/* Task modal */}
      <TaskModal
        mode={taskModalMode}
        taskId={taskModalId}
        isOpen={taskModalOpen}
        onClose={handleTaskModalClose}
        onSave={handleTaskModalSave}
      />

      {/* Change Status modal */}
      {changeStatusOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-3">Change Status</h3>
            <p className="text-sm text-gray-600 mb-4">
              Update {selectedIds.size} task(s) to:
            </p>
            <div className="space-y-2 mb-4">
              {(["active", "test", "inactive"] as const).map((opt) => (
                <label key={opt} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="changeStatus"
                    checked={changeStatusValue === opt}
                    onChange={() => setChangeStatusValue(opt)}
                  />
                  <span className="capitalize">{opt}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setChangeStatusOpen(false)}
                className="px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBatchChangeStatus}
                disabled={batchSaving}
                className="px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {batchSaving ? "Saving..." : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
